import { z } from 'zod';
import { PersonRef } from './people.js';
import { addDaysIso, zonedDate, zonedToUtc } from './time.js';
/**
 * Birthdays (docs/adr/0047-birthdays-and-contact-visibility.md). A person may give a day and a month,
 * never a year: the schema is strict, so a body that carries a year is refused rather than trimmed, and
 * the database has no column that could hold one. Celebrations are off for a workspace until an owner or
 * admin turns on `privacy.birthdays`, and each person can opt out with "Celebrate my birthday".
 */
export const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
];
/** Days in a month, with 29 for February (a 29 February birthday is celebrated on the 28th in other years). */
export function daysInBirthdayMonth(month) {
    return month === 2 ? 29 : [4, 6, 9, 11].includes(month) ? 30 : 31;
}
export const Birthday = z
    .strictObject({
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
})
    .refine((b) => b.day <= daysInBirthdayMonth(b.month), { message: 'That day is not in that month', path: ['day'] });
const isLeap = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
/** True when `date` ('YYYY-MM-DD') is this birthday. 29 February falls on the 28th outside leap years. */
export function isBirthdayOn(date, b) {
    const [y, m, d] = date.split('-').map(Number);
    if (m !== b.month)
        return false;
    if (d === b.day)
        return true;
    return b.month === 2 && b.day === 29 && d === 28 && !isLeap(y);
}
/** "14 March". No year, ever. */
export function formatBirthday(b) {
    return `${b.day} ${MONTH_NAMES[b.month - 1]}`;
}
/**
 * The person's birthday as it falls at `now` on their own clock: the local date and the instants it starts
 * and ends, or null when it is not their birthday there. Used by the worker to open a celebration.
 */
export function birthdayWindow(now, timeZone, b) {
    const date = zonedDate(now, timeZone);
    if (!isBirthdayOn(date, b))
        return null;
    const [y, m, d] = date.split('-').map(Number);
    const [ny, nm, nd] = addDaysIso(date, 1).split('-').map(Number);
    return { date, startsAt: zonedToUtc(y, m, d, 0, 0, timeZone), endsAt: zonedToUtc(ny, nm, nd, 0, 0, timeZone) };
}
/** One teammate whose birthday it is today, on their own clock. */
export const BirthdayToday = z.object({ person: PersonRef });
export const BirthdaysToday = z.object({ items: z.array(BirthdayToday) });
/** The card's words, shared by web and phone: "It's Sam's birthday" and the nudge. No gendered pronouns. */
export function birthdayHeadline(names) {
    const first = (n) => n.trim().split(/\s+/)[0] ?? n;
    if (names.length === 0)
        return '';
    if (names.length === 1)
        return `It's ${first(names[0])}'s birthday`;
    if (names.length === 2)
        return `It's ${first(names[0])} and ${first(names[1])}'s birthday`;
    return `It's ${names.slice(0, -1).map(first).join(', ')} and ${first(names[names.length - 1])}'s birthday`;
}
export const BIRTHDAY_NUDGE = 'Send them a happy birthday';
//# sourceMappingURL=birthdays.js.map